import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { APP_STATUS, APP_TYPE, getPublicCatalog } from '../constants/apps';

const Navbar = ({ onDownloadClick }) => {
  const { lang } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n, t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleLanguageChange = (newLang) => {
    i18n.changeLanguage(newLang);
    setIsMenuOpen(false);
    const currentPath = location.pathname.replace(`/${lang}`, '') || '/';
    navigate(`/${newLang}${currentPath === '/' ? '' : currentPath}`);
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const catalogItems = getPublicCatalog();
  const appItems = catalogItems.filter((item) => item.type === APP_TYPE.APP);
  const toolItems = catalogItems.filter((item) => item.type === APP_TYPE.TOOL);

  const mapItemToNav = (item) => {
    const isLive = item.status === APP_STATUS.LIVE;
    const pathBase = item.type === APP_TYPE.TOOL ? 'tools' : 'apps';
    return {
      id: item.id,
      name: item.name,
      icon: item.icon,
      type: isLive ? 'link' : 'action',
      path: `/${lang}/${pathBase}/${item.pageRoute}`,
      badge: isLive ? null : t('nav.soon')
    };
  };

  const navAppItems = appItems.map(mapItemToNav);
  const navToolItems = toolItems.map(mapItemToNav);

  const renderItem = (product) => {
    if (product.type === 'action') {
      return (
        <button
          key={product.id}
          onClick={() => {
            setIsDropdownOpen(false);
            onDownloadClick(product.id);
          }}
          className="dropdown-item"
          style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left' }}
        >
          <span className="item-icon">{product.icon}</span>
          <div className="item-info">
            <span className="item-name">{product.name}</span>
            {product.badge && <span className="item-badge">{product.badge}</span>}
          </div>
        </button>
      );
    }

    return (
      <Link
        key={product.id}
        to={product.path}
        className="dropdown-item"
        onClick={() => setIsDropdownOpen(false)}
      >
        <span className="item-icon">{product.icon}</span>
        <div className="item-info">
          <span className="item-name">{product.name}</span>
          {product.badge && <span className="item-badge">{product.badge}</span>}
        </div>
      </Link>
    );
  };

  return (
    <nav className="navbar">
      <div className="container nav-container">
        <Link to={`/${lang}`} className="logo-group" aria-label="NPU Universe Home" onClick={closeMenu}>
          <img src="/assets/logo.png" alt="NPU Universe Logo - Shaping the Future of Local AI" />
          <span>NPU Universe</span>
        </Link>
        
        {/* Desktop Actions */}
        <div className="nav-actions">
            {/* Products Dropdown */}
            <div 
              className="nav-dropdown-wrapper"
              onMouseEnter={() => setIsDropdownOpen(true)}
              onMouseLeave={() => setIsDropdownOpen(false)}
            >
              <button className={`btn btn-secondary dropdown-trigger ${isDropdownOpen ? 'active' : ''}`}>
                {t('nav.products')} <span className="arrow">▾</span>
              </button>
              
              <div className={`nav-dropdown-menu glass-card ${isDropdownOpen ? 'open' : ''}`}>
                <Link to={`/${lang}/apps`} className="dropdown-section-label" onClick={() => setIsDropdownOpen(false)} style={{ textDecoration: 'none', display: 'block' }}>
                  {t('nav.apps', { defaultValue: 'Apps' })} →
                </Link>
                {navAppItems.map(renderItem)}
                {navToolItems.length > 0 && <div className="mobile-divider" style={{ margin: '10px 0' }} />}
                {navToolItems.length > 0 && <Link to={`/${lang}/tools`} className="dropdown-section-label" onClick={() => setIsDropdownOpen(false)} style={{ textDecoration: 'none', display: 'block' }}>
                  {t('nav.tools', { defaultValue: 'Tools' })} →
                </Link>}
                {navToolItems.map(renderItem)}
              </div>
            </div>

            <button className={`btn btn-secondary dropdown-trigger ${location.pathname.includes('/company') ? 'active' : ''}`} onClick={() => navigate(`/${lang}/company`)}>
              {t('company.title')}
            </button>

            <button className={`btn btn-secondary dropdown-trigger ${location.pathname.includes('/changelog') ? 'active' : ''}`} onClick={() => navigate(`/${lang}/changelog`)}>
              {t('nav.changelog', { defaultValue: 'Changelog' })}
            </button>
            
            <button 
              onClick={() => {
                const isBenchmark = location.pathname.includes('/benchmark');
                if (isBenchmark) {
                  navigate(`/${lang}/apps/npu-check#downloads`);
                } else {
                  navigate(`/${lang}/download`);
                }
              }} 
              className="btn btn-primary" 
              style={{ padding: '8px 25px', fontSize: '0.9rem' }}
            >
              {t('nav.download')}
            </button>
            
            {/* Language Switcher */}
            <div className="lang-switcher">
              <button onClick={() => handleLanguageChange('en')} className={i18n.language === 'en' ? 'active' : ''}>EN</button>
              <button onClick={() => handleLanguageChange('es')} className={i18n.language === 'es' ? 'active' : ''}>ES</button>
            </div>
        </div>

        {/* Hamburger Icon */}
        <button className={`hamburger ${isMenuOpen ? 'open' : ''}`} onClick={toggleMenu} aria-label={isMenuOpen ? "Close Menu" : "Open Menu"}>
          <span></span><span></span><span></span>
        </button>
      </div>

      {/* Mobile Overlay Menu */}
      <div className={`nav-mobile-overlay ${isMenuOpen ? 'open' : ''}`}>
         <nav style={{ display: 'flex', flexDirection: 'column' }}>
           {navAppItems.map((product) => {
             if (product.type === 'action') {
               return (
                 <button
                   key={product.id}
                   onClick={() => {
                     onDownloadClick(product.id);
                     closeMenu();
                   }}
                   className="mobile-nav-link mobile-nav-button"
                 >
                   {product.icon} {product.name} {product.badge && <span className="mobile-badge">{product.badge}</span>}
                 </button>
               );
             }

             return (
               <Link key={product.id} to={product.path} onClick={closeMenu} className="mobile-nav-link">
                 {product.icon} {product.name}
               </Link>
             );
           })}
            
            {/* Separador */}
            <div className="mobile-divider"></div>
            {navToolItems.map((tool) => (
              <Link key={tool.id} to={tool.path} onClick={closeMenu} className="mobile-nav-link">
                {tool.icon} {tool.name}
              </Link>
            ))}
            
            {/* Separador */}
            <div className="mobile-divider"></div>
           
            {/* Misión y Descargar */}
             <button onClick={() => { navigate(`/${lang}/company`); closeMenu(); }} className="mobile-nav-link mobile-nav-button">
               {t('company.title')}
             </button>
             <button onClick={() => { navigate(`/${lang}/changelog`); closeMenu(); }} className="mobile-nav-link mobile-nav-button">
               {t('nav.changelog', { defaultValue: 'Changelog' })}
             </button>
             <button 
                onClick={() => { navigate(`/${lang}/download`); closeMenu(); }} 
                className="mobile-nav-link mobile-nav-button mobile-nav-download"
             >
               {t('nav.download_app')} ↗
             </button>
            
            {/* Selector de idioma móvil */}
            <div className="mobile-lang-footer">
              <span className="mobile-lang-label">Idioma / Language</span>
              <div className="lang-switcher mobile-lang-switcher">
                <button 
                  onClick={() => handleLanguageChange('en')} 
                  className={i18n.language === 'en' ? 'active' : ''}
                >
                  EN
                </button>
                <button 
                  onClick={() => handleLanguageChange('es')} 
                  className={i18n.language === 'es' ? 'active' : ''}
                >
                  ES
                </button>
              </div>
            </div>
          </nav>
      </div>

      <style>{`
        .nav-dropdown-wrapper { position: relative; height: 100%; display: flex; align-items: center; }
        .dropdown-trigger { display: flex; alignItems: center; gap: 8px; border: none !important; }
        .dropdown-trigger.active { background: rgba(255,255,255,0.05); }
        .arrow { font-size: 0.7rem; transition: transform 0.3s; }
        .dropdown-trigger.active .arrow { transform: rotate(180deg); }
        
        .nav-dropdown-menu {
          position: absolute;
          top: calc(100% + 15px);
          left: 50%;
          transform: translateX(-50%) translateY(10px);
          width: 280px;
          padding: 15px !important;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
          z-index: 1000;
          background: rgba(13, 14, 18, 0.95) !important;
          backdrop-filter: blur(20px) !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          box-shadow: 0 20px 40px rgba(0,0,0,0.4);
        }
        .nav-dropdown-menu::before {
          content: '';
          position: absolute;
          top: -20px;
          left: 0;
          right: 0;
          height: 20px;
        }
        .nav-dropdown-menu.open {
          opacity: 1;
          visibility: visible;
          transform: translateX(-50%) translateY(0);
        }
        .dropdown-item {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 12px;
          border-radius: 12px;
          color: white;
          text-decoration: none;
          transition: background 0.2s;
        }
        .dropdown-item:hover { background: rgba(255,255,255,0.05); }
        .item-icon { fontSize: 1.2rem; }
        .item-info { display: flex; flex-direction: column; }
        .item-name { font-weight: 600; font-size: 0.95rem; }
        .item-badge { font-size: 0.65rem; color: var(--primary-cyan); font-weight: 800; text-transform: uppercase; }
        .dropdown-section-label {
          font-size: 0.7rem;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 1px;
          padding: 8px 12px;
          font-weight: 700;
        }
        
        .lang-switcher { display: flex; gap: 4px; background: rgba(255,255,255,0.05); padding: 4px; borderRadius: 8px; }
        .lang-switcher button { 
          min-width: 32px; padding: 4px 8px; font-size: 0.75rem; border: none; cursor: pointer; 
          background: transparent; color: white; border-radius: 6px; transition: all 0.2s; 
        }
        .lang-switcher button.active { background: var(--primary-purple); font-weight: 800; }
        .mobile-divider { height: 1px; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1) 20%, rgba(255,255,255,0.1) 80%, transparent); margin: 15px 0; }
        .mobile-badge { font-size: 0.6rem; background: var(--primary-purple); padding: 2px 6px; border-radius: 10px; margin-left: 10px; }
        .mobile-nav-button { color: white; background: none; border: none; text-align: left; width: 100%; cursor: pointer; }
        .mobile-nav-download { color: var(--primary-cyan); }
      `}</style>
    </nav>
  );
};

export default Navbar;
